/**
 * ERPLookupModal — Reusable search popup
 * Uses erp-theme.css classes ONLY. No inline styles.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Search, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';

const ERPLookupModal = ({ title, columns, fetchData, onSelect, onClose, onAdd }) => {
  const [search, setSearch] = useState('');
  const [searchField, setSearchField] = useState(columns[0]?.field || '');
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const result = await fetchData({ search, searchField, page, pageSize });
      setData(result.data || []);
      setTotal(result.total || result.data?.length || 0);
    } catch { setData([]); }
    setLoading(false);
  }, [fetchData, search, searchField, page, pageSize]);

  useEffect(() => { loadData(); }, [loadData]);
  const totalPages = Math.ceil(total / pageSize) || 1;

  return (
    <div className="erp-modal-overlay" data-testid="erp-lookup-modal">
      <div className="erp-modal">
        <div className="erp-modal-header">
          <span className="erp-modal-title">{title}</span>
          <div className="erp-screen-actions">
            {onAdd && <button className="erp-btn erp-btn-primary" onClick={onAdd} data-testid="lookup-add-btn">F2</button>}
            <button className="erp-modal-close" onClick={onClose} data-testid="lookup-close-btn">X</button>
          </div>
        </div>
        <div className="erp-modal-search">
          <span className="erp-form-label">Find</span>
          <input value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            placeholder="Search..." className="erp-input" data-testid="lookup-search-input" autoFocus />
          <select value={searchField} onChange={(e) => setSearchField(e.target.value)} className="erp-input erp-select">
            {columns.map(c => <option key={c.field} value={c.field}>{c.header}</option>)}
          </select>
        </div>
        <div className="erp-modal-body">
          <table className="erp-grid">
            <thead>
              <tr>{columns.map(c => <th key={c.field}>{c.header}</th>)}</tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={columns.length} className="cell-muted" style={{padding:'32px',textAlign:'center'}}>Loading...</td></tr>
              ) : data.length === 0 ? (
                <tr><td colSpan={columns.length} className="cell-muted" style={{padding:'32px',textAlign:'center'}}>No records found</td></tr>
              ) : data.map((row, i) => (
                <tr key={i} onClick={() => { setSelected(row); onSelect(row); }}
                  className={selected === row ? 'selected' : ''} data-testid={`lookup-row-${i}`}>
                  {columns.map(c => <td key={c.field}>{row[c.field] || ''}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="erp-pagination">
          <span>Page {page} of {total} Records</span>
          <div className="erp-pagination-controls">
            <span>Show Rows {pageSize}</span>
            <span>Page</span>
            <input type="number" value={page} onChange={(e) => setPage(Math.max(1, Math.min(totalPages, parseInt(e.target.value) || 1)))} />
            <span>of {totalPages}</span>
            <button className="erp-pagination-btn" onClick={() => setPage(1)} disabled={page <= 1}><ChevronsLeft size={11} /></button>
            <button className="erp-pagination-btn" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1}><ChevronLeft size={11} /></button>
            <button className="erp-pagination-btn" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages}><ChevronRight size={11} /></button>
            <button className="erp-pagination-btn" onClick={() => setPage(totalPages)} disabled={page >= totalPages}><ChevronsRight size={11} /></button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ERPLookupModal;
